import React from "react";
import ArticleLayout, { Step, Tip, CodeBlock, Check, Ext } from "./ArticleLayout";

export default function WhatsAppGuide() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://agentreply.io";
  return (
    <ArticleLayout
      kicker="How-to guide"
      title="Connect your agent to WhatsApp."
      intro="Two paths: a 5-minute sandbox for testing, then a real WhatsApp Business number for going live."
    >
      <div id="sandbox">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3">Path 1 — Sandbox (5 min, free)</div>
        <h2 className="font-serif text-3xl mb-3">Test in the Twilio sandbox.</h2>
        <p className="font-mono text-sm text-[#A39A8F] mb-10">Twilio gives every account a shared WhatsApp number you can use immediately. Great for testing — but only people who join the sandbox can chat with you.</p>

        <Step n="1" title="Create a Twilio account">
          <p>Head to <Ext href="https://www.twilio.com/try-twilio">twilio.com/try-twilio</Ext> and sign up. Free credit included (~$15).</p>
        </Step>
        <Step n="2" title="Activate the WhatsApp sandbox">
          <p>In the Twilio Console: <strong className="text-[#F5F2EB]">Messaging → Try it out → Send a WhatsApp message</strong>.</p>
          <p>You'll see a sandbox number (e.g. <code className="text-[#D95B38]">+1 415 523 8886</code>) and a join code (e.g. <code className="text-[#D95B38]">join red-mountain</code>). Send that code from your personal WhatsApp to the sandbox number.</p>
          <Tip>Anyone you want to test with needs to send the join code first. The sandbox is a closed group.</Tip>
        </Step>
        <Step n="3" title="Grab your three credentials">
          <p>In the Twilio Console: <strong className="text-[#F5F2EB]">Account → API keys & tokens</strong>. Copy:</p>
          <ul className="list-none space-y-1 pl-1">
            <Check><strong className="text-[#F5F2EB]">Account SID</strong> — starts with <code>AC...</code></Check>
            <Check><strong className="text-[#F5F2EB]">Auth Token</strong> — long random string</Check>
            <Check><strong className="text-[#F5F2EB]">WhatsApp number</strong> — the sandbox number (e.g. <code>whatsapp:+14155238886</code>)</Check>
          </ul>
        </Step>
        <Step n="4" title="Paste them into AgentReply">
          <p>Open your agent → <strong className="text-[#F5F2EB]">WhatsApp</strong> tab. Paste & save. Status flips to <span className="text-[#D95B38]">Connected</span>.</p>
        </Step>
        <Step n="5" title="Configure the Twilio webhook">
          <p>Back in Twilio sandbox settings → <strong className="text-[#F5F2EB]">"When a message comes in"</strong>:</p>
          <CodeBlock>{`${origin}/api/whatsapp/inbound`}</CodeBlock>
          <p>HTTP method: <code className="text-[#D95B38]">POST</code>. Save.</p>
        </Step>
        <Step n="6" title="Send a test message">
          <p>From your phone, message the sandbox number. Your agent replies within seconds.</p>
          <Tip>Nothing happens after 30 seconds? Check Twilio Console → Monitor → Logs → Errors. Usually a webhook typo.</Tip>
        </Step>
      </div>

      <div id="live" className="pt-16 mt-16 border-t border-[#F5F2EB]/10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3">Path 2 — Go live (1-3 days, paid)</div>
        <h2 className="font-serif text-3xl mb-3">Use your business WhatsApp number.</h2>
        <p className="font-mono text-sm text-[#A39A8F] mb-10">Get a real WhatsApp Business number under your brand. Customers message you without joining anything.</p>

        <Step n="1" title="Verified Meta Business account">
          <p>Set up at <Ext href="https://business.facebook.com">business.facebook.com</Ext>. Verification needs business name, address, website, and a phone bill or registration document.</p>
        </Step>
        <Step n="2" title="Request a WhatsApp Sender in Twilio">
          <p>Twilio Console → <strong className="text-[#F5F2EB]">Messaging → Senders → WhatsApp senders → Create new sender</strong>.</p>
          <ul className="list-none space-y-1 pl-1">
            <Check>A phone number you control (NOT already attached to a personal WhatsApp)</Check>
            <Check>Your Meta Business ID</Check>
            <Check>Display name (e.g. "AgentReply Support")</Check>
          </ul>
        </Step>
        <Step n="3" title="Wait for Meta approval">
          <p>Usually 1-3 business days. You'll get an email when approved.</p>
        </Step>
        <Step n="4" title="Swap sandbox for live number">
          <p>In AgentReply → WhatsApp tab → replace sandbox number with your live one (e.g. <code>whatsapp:+447700900123</code>). Set the webhook in your new sender's settings:</p>
          <CodeBlock>{`${origin}/api/whatsapp/inbound`}</CodeBlock>
        </Step>
        <Step n="5" title="Promote your number">
          <ul className="list-none space-y-1 pl-1">
            <Check>Add <code>https://wa.me/&lt;your-number&gt;</code> to your site, email signature, Instagram bio</Check>
            <Check>Generate a QR code (free in Twilio sender settings)</Check>
            <Check>Add the number to your Google Business profile</Check>
          </ul>
        </Step>
      </div>
    </ArticleLayout>
  );
}
